/**
 * Profound Philosophy — Sofia, the Socratic companion.
 *
 * Ported from Profound Living's Willow page (the reference coaching client)
 * and re-toned for this app's cloud-studies canvas. Structure is identical:
 * intro → consent gate → conversation, with the thread carried across
 * navigation and reloads via sessionStorage.
 *
 * Australian English throughout.
 */

import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Send, Download, Trash2, LogIn, HelpCircle, RotateCcw, Settings as SettingsIcon, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import companionService from '../services/companionService';
import {
  CrisisResourcesCard,
  ProfessionalSupportCard,
  SubtleProfessionalHint,
  CompanionDisclaimer,
  UsageIndicator,
  ConsentGate,
} from '../components/companion/CompanionPanels';
import { renderMarkdown } from '../components/companion/renderMarkdown.jsx';
import SofiaIntro from '../components/companion/SofiaIntro';
import SofiaAvatar from '../components/companion/SofiaAvatar';

const IS_NATIVE = Capacitor.isNativePlatform();

// One prompt per mode of her contract: examine, map, steelman, companion —
// so the empty screen teaches the range before she's said a word.
const suggestedPrompts = [
  'I think people are basically selfish. Examine me.',
  'Where does the free will debate actually stand? Teach me the map.',
  'Help me build the strongest version of a view I can’t stand.',
  'Test what I argued today against the positions I’ve recorded.',
];

// sessionStorage, not localStorage, and deliberately: a conversation about
// what you believe shouldn't outlive the browser tab on a shared machine. The
// durable copy is the transcript the Hub keeps against the account, which can
// be exported or deleted from the "What Sofia keeps" panel.
const THREAD_KEY = 'pp_sofia_thread_v1';
const THREAD_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const readThread = (uid) => {
  if (!uid) return null;
  const key = `${THREAD_KEY}:${uid}`;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    const usable =
      Array.isArray(saved?.messages) &&
      saved.messages.length > 0 &&
      saved.savedAt &&
      Date.now() - saved.savedAt <= THREAD_MAX_AGE_MS;
    if (!usable) { sessionStorage.removeItem(key); return null; }
    return saved;
  } catch {
    try { sessionStorage.removeItem(key); } catch { /* nothing to do */ }
    return null;
  }
};

const writeThread = (uid, payload) => {
  if (!uid) return;
  try {
    sessionStorage.setItem(`${THREAD_KEY}:${uid}`, JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch {
    /* private mode / quota — the conversation still works, it just won't resume */
  }
};

const clearThread = (uid) => {
  if (!uid) return;
  try { sessionStorage.removeItem(`${THREAD_KEY}:${uid}`); } catch { /* nothing to do */ }
};

// The module the reader came from travels with every message, so Sofia knows
// what they were reading (the Knowledge Hub renders it as module context).
// sessionStorage, like the seed, so it survives the sign-in redirect; the
// reader can clear it, and a new conversation clears it too.
const MODULE_KEY = 'profoundPhilosophy_companionModule';
const MODULE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const readModuleContext = (fromState) => {
  try {
    if (fromState && fromState.id != null && fromState.title) {
      const ctx = { id: fromState.id, title: fromState.title, section: fromState.section || null };
      sessionStorage.setItem(MODULE_KEY, JSON.stringify({ ...ctx, t: Date.now() }));
      return ctx;
    }
    const raw = sessionStorage.getItem(MODULE_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved?.title || Date.now() - (saved.t || 0) > MODULE_MAX_AGE_MS) {
      sessionStorage.removeItem(MODULE_KEY);
      return null;
    }
    return { id: saved.id, title: saved.title, section: saved.section || null };
  } catch {
    return fromState?.title ? { id: fromState.id, title: fromState.title, section: fromState.section || null } : null;
  }
};

export default function CompanionPage() {
  const { user, isAuthenticated, loading: authLoading, redirectToAuth } = useAuth();
  const uid = user?.uid || null;
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  // A sparring prompt elsewhere in the app can hand over a seeded opener via
  // navigation state. Prefill only - never auto-send: the user finishes the
  // sentence and decides whether to press send.
  const seededLocation = useLocation();
  // Arrived from a module's sparring prompt: remember which module, so every
  // message carries it (see readModuleContext above).
  const [fromModule, setFromModule] = useState(() => readModuleContext(seededLocation.state?.fromModule));
  const clearModuleContext = () => {
    try { sessionStorage.removeItem(MODULE_KEY); } catch { /* nothing to do */ }
    setFromModule(null);
  };
  // The sign-in gate redirects through the auth hub (a full page unload), so
  // the seed is parked in sessionStorage with a 15-minute shelf life and
  // consumed on first send. Signed-out readers keep their opener.
  React.useEffect(() => {
    try {
      const fromState = seededLocation.state?.seed;
      if (fromState && typeof fromState === 'string') {
        sessionStorage.setItem('profoundPhilosophy_companionSeed', JSON.stringify({ seed: fromState, t: Date.now() }));
        window.history.replaceState({}, '');
      }
      const raw = sessionStorage.getItem('profoundPhilosophy_companionSeed');
      if (raw) {
        const { seed, t } = JSON.parse(raw);
        if (seed && Date.now() - t < 15 * 60 * 1000) {
          setInput((current) => current || seed);
        } else {
          sessionStorage.removeItem('profoundPhilosophy_companionSeed');
        }
      }
    } catch { /* storage unavailable: the seed is a convenience, not a dependency */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [crisis, setCrisis] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  // The intro comes first for anyone who hasn't consented yet — nobody should
  // be asked for their name and country before they know who is asking.
  const [introDone, setIntroDone] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  // "What Sofia keeps" — export and delete controls, keeping the consent
  // gate's read-export-delete promise honest without a separate route.
  const [showMemory, setShowMemory] = useState(false);
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [memoryNote, setMemoryNote] = useState(null);
  // Messages restored from a previous visit render without an entrance
  // animation — framer entrance states are the standing trap in this app.
  const [restoredCount, setRestoredCount] = useState(0);
  const bottomRef = useRef(null);

  // Don't ask the Hub for a profile until there's an identity to ask about.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoadingProfile(false); return; }
    (async () => {
      const res = await companionService.getProfile();
      if (res?.success) setProfile(res.profile);
      setLoadingProfile(false);
    })();
  }, [isAuthenticated, authLoading]);

  // Pick the thread back up, across in-app navigation and across a reload.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!uid) return;
    const saved = readThread(uid);
    hydrated.current = true;
    if (!saved) return;
    companionService.restore({
      sessionId: saved.sessionId,
      conversationHistory: saved.conversationHistory,
    });
    setMessages(saved.messages);
    setRestoredCount(saved.messages.length);
    setSessionStarted(Boolean(saved.sessionId));
  }, [uid]);

  // Save after every exchange, including the service's own session id and
  // history. Gated on `hydrated` so the mount render can't clear the very
  // thread that was about to be restored.
  useEffect(() => {
    if (!uid || !hydrated.current) return;
    if (!messages.length) { clearThread(uid); return; }
    writeThread(uid, { messages, ...companionService.snapshot() });
  }, [uid, messages]);

  const startNewConversation = async () => {
    await companionService.endSession().catch(() => {});
    companionService.resetSession();
    clearThread(uid);
    clearModuleContext();
    setMessages([]);
    setRestoredCount(0);
    setCrisis(null);
    setProfessional(null);
    setError(null);
    setSessionStarted(false);
  };

  // Only chase the bottom once a conversation exists, and stop at the end of
  // the messages rather than the end of the page (the composer is fixed).
  useEffect(() => {
    if (!messages.length && !crisis && !professional) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, crisis, professional]);

  const handleGrantConsent = async (opts) => {
    const res = await companionService.grantConsent(opts);
    if (res?.success) setProfile(res.profile);
    return res;
  };

  const ensureSession = async () => {
    if (sessionStarted) return;
    await companionService.startSession({ sessionType: 'exploration' });
    setSessionStarted(true);
  };

  const send = async (text) => {
    try { sessionStorage.removeItem('profoundPhilosophy_companionSeed'); } catch { /* noop */ }
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    await ensureSession();
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');

    const res = await companionService.askCoach(trimmed, fromModule ? {
      currentModule: String(fromModule.id),
      moduleName: fromModule.title,
      sectionName: fromModule.section || null,
    } : {});
    if (res?.success) {
      setMessages((prev) => [...prev, { role: 'assistant', content: res.response }]);
      setCrisis(res.crisisResources || null);
      setProfessional(res.professionalSupport || null);
      setUsage(res.usage || null);
    } else {
      setError(res?.response || 'Something went wrong.');
      if (res?.dailyCapExceeded) {
        setUsage({
          tier: res.tier, dailyLimit: res.dailyLimit, used: res.dailyLimit,
          remaining: 0, upgradeUrl: res.upgradeUrl,
        });
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: res?.response || '…', isError: true }]);
    }
    setSending(false);
  };

  const exportMemory = async () => {
    setMemoryBusy(true);
    setMemoryNote(null);
    const res = await companionService.exportData();
    if (res?.success !== false) {
      try {
        const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sofia-export.json';
        a.click();
        URL.revokeObjectURL(url);
        setMemoryNote('Exported. Check your downloads.');
      } catch {
        setMemoryNote('The export arrived but couldn’t be saved as a file. Please try again.');
      }
    } else {
      setMemoryNote(res.response || 'That didn’t work. Please try again in a moment.');
    }
    setMemoryBusy(false);
  };

  const deleteMemory = async () => {
    // A browser confirm is deliberate — deleting means gone, and this is the
    // one click on the page that cannot be walked back.
    if (!window.confirm('Delete everything Sofia keeps about you? This cannot be undone.')) return;
    setMemoryBusy(true);
    setMemoryNote(null);
    const res = await companionService.deleteAll();
    if (res?.success !== false) {
      clearThread(uid);
      clearModuleContext();
      setMessages([]);
      setRestoredCount(0);
      setSessionStarted(false);
      setUsage(null);
      setProfile(null);
      setShowMemory(false);
      setIntroDone(false);
    } else {
      setMemoryNote(res.response || 'That didn’t work. Please try again in a moment.');
    }
    setMemoryBusy(false);
  };

  const suppressUpgrade = Boolean(crisis) || professional?.tone === 'amber';

  if (authLoading || loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal flex items-center justify-center">
        <p className="text-pearl/70">One moment…</p>
      </div>
    );
  }

  // Sofia is account-only. She keeps what you believe and how you argued it,
  // and an anonymous session that evaporates when the browser is cleared means
  // the person has confided their thinking for nothing.
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-32 pb-16 px-4 sm:px-6">
        <div className="max-w-xl mx-auto bg-charcoal border border-white/10 rounded-2xl p-6 sm:p-8 text-center">
          <div className="flex justify-center mb-4">
            <SofiaAvatar size={48} />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-3">Sofia needs to know who you are</h1>
          <p className="text-sm sm:text-base text-pearl/90 leading-relaxed mb-6">
            Not for the sake of it: she remembers what you believe and how the argument went last
            time, and that only works if there&apos;s an account to remember it against. You can
            delete everything she holds at any time.
          </p>
          <button
            onClick={() => redirectToAuth('signin')}
            className="inline-flex items-center gap-2 px-5 py-3 bg-gold hover:bg-bright-gold text-gunmetal rounded-lg font-semibold transition-colors"
          >
            <LogIn size={18} /> Sign in to talk to Sofia
          </button>
        </div>
      </div>
    );
  }

  if (!profile?.consent?.granted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-32 pb-16 px-4 sm:px-6">
        {introDone
          ? <ConsentGate onAccept={handleGrantConsent} />
          : <SofiaIntro onContinue={() => setIntroDone(true)} />}
      </div>
    );
  }

  if (showIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-32 pb-16 px-4 sm:px-6">
        <SofiaIntro
          returning
          hasThread={messages.length > 0}
          onContinue={() => setShowIntro(false)}
        />
      </div>
    );
  }

  // The composer only pins to the viewport once there is a conversation to
  // scroll — on an empty screen a fixed bar sits marooned at the bottom.
  //
  // NB this comment lives ABOVE the return on purpose: a {/* */} comment as a
  // sibling of the return root is the documented page-blanking trap here.
  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-24 md:pt-28 pb-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SofiaAvatar size={40} />
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-white">Sofia</h1>
              <p className="text-xs sm:text-sm text-pearl/70">
                Generous with the map. The conclusions stay yours.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={startNewConversation}
                className="p-2 rounded-lg hover:bg-white/10 text-pearl/70 hover:text-white transition-colors"
                title="Start a new conversation"
                aria-label="Start a new conversation"
              >
                <RotateCcw size={18} />
              </button>
            )}
            <button
              onClick={() => setShowIntro(true)}
              className="p-2 rounded-lg hover:bg-white/10 text-pearl/70 hover:text-white transition-colors"
              title="About Sofia"
              aria-label="About Sofia"
            >
              <HelpCircle size={18} />
            </button>
            <button
              onClick={() => { setShowMemory(!showMemory); setMemoryNote(null); }}
              className="p-2 rounded-lg hover:bg-white/10 text-pearl/70 hover:text-white transition-colors"
              title="What Sofia keeps"
              aria-label="What Sofia keeps"
            >
              <SettingsIcon size={18} />
            </button>
          </div>
        </header>

        {showMemory && (
          <div className="mb-4 bg-gunmetal border border-white/10 rounded-xl p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-sm font-semibold text-bright-gold">What Sofia keeps</h2>
              <button
                onClick={() => setShowMemory(false)}
                className="p-1 rounded hover:bg-white/10 text-pearl/60 hover:text-white"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-sm text-pearl/80 leading-relaxed mb-3">
              A small profile: what you&apos;ve told her about yourself, your conversations with her
              and a summary of each, and the positions you&apos;ve taken. It stays with this app. Export a
              copy, or delete the lot; deleting means gone, and she&apos;ll meet you as a stranger
              next time.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={exportMemory}
                disabled={memoryBusy}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-white/15 text-pearl hover:bg-white/10 text-sm font-medium disabled:opacity-60 transition-colors"
              >
                <Download size={15} /> Export a copy
              </button>
              <button
                onClick={deleteMemory}
                disabled={memoryBusy}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-400/50 text-red-300 hover:bg-red-950/40 text-sm font-medium disabled:opacity-60 transition-colors"
              >
                <Trash2 size={15} /> Delete everything
              </button>
            </div>
            {memoryNote && <p className="text-xs sm:text-sm text-pearl/70 mt-2">{memoryNote}</p>}
          </div>
        )}

        <div className="mb-3 space-y-2">
          <CompanionDisclaimer />
          <UsageIndicator usage={usage} suppressUpgrade={suppressUpgrade} />
          {fromModule && (
            <p className="text-xs sm:text-sm text-pearl/75 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                Sofia knows you came from <span className="text-pearl">{fromModule.title}</span>
                {fromModule.section ? <>, on &ldquo;{fromModule.section}&rdquo;</> : null}.
              </span>
              <button
                type="button"
                onClick={clearModuleContext}
                className="text-bright-gold hover:text-white underline underline-offset-2"
              >
                Talk about something else
              </button>
            </p>
          )}
        </div>

        {messages.length === 0 && (
          <div className="bg-charcoal border border-white/10 rounded-2xl p-5 mb-4">
            <p className="text-sm sm:text-base text-pearl/90 leading-relaxed">
              Hello {profile?.personalContext?.firstName || 'there'}. Good. You came back. Tell me
              something you believe, something you&apos;re stuck on, or a debate you want the map of.
              An opinion you&apos;re certain about is an excellent place to start; certainty gives us
              the most to work with.
            </p>
            <p className="text-xs sm:text-sm text-pearl/60 mt-3">
              Bring a claim rather than a topic: she works best on something you&apos;d actually
              defend, and she&apos;s read every module in here and knows the positions you&apos;ve recorded.{' '}
              <button
                onClick={() => setShowIntro(true)}
                className="text-bright-gold hover:text-white underline underline-offset-2"
              >
                More about Sofia
              </button>
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {suggestedPrompts.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="text-left text-sm sm:text-base text-pearl/90 bg-gunmetal hover:bg-gold/15 hover:text-bright-gold border border-white/10 rounded-lg p-3 transition-colors"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4" role="log" aria-live="polite">
          <AnimatePresence>
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={i < restoredCount ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm sm:text-base leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-gold text-gunmetal font-medium'
                      : m.isError
                        ? 'bg-red-950/60 text-red-100 border border-red-500/60'
                        : 'bg-charcoal text-pearl border border-white/10'
                  }`}
                >
                  {m.role === 'assistant' && !m.isError ? renderMarkdown(m.content) : m.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {crisis && <CrisisResourcesCard resources={crisis} />}
          {!crisis && professional?.tone === 'amber' && <ProfessionalSupportCard support={professional} />}
          {!crisis && professional?.tone === 'subtle' && <SubtleProfessionalHint support={professional} />}

          {sending && (
            <div className="flex justify-start" role="status">
              <div className="bg-charcoal border border-white/10 rounded-2xl px-4 py-3 text-pearl/70 text-sm">
                Sofia is thinking…
              </div>
            </div>
          )}
          {/* Spacer so the newest message clears the fixed composer. */}
          <div ref={bottomRef} className="h-4" />
        </div>
      </div>

      {/* On iOS the tab bar owns the bottom edge, so the composer sits on top of it.
          The page already clears both: its own pb-28 plus Layout's native spacer. */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className={`fixed left-0 right-0 bg-gunmetal border-t border-white/10 px-4 sm:px-6 pt-3 z-40 ${
          IS_NATIVE
            ? 'bottom-[calc(4rem+env(safe-area-inset-bottom))] pb-3'
            : 'bottom-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]'
        }`}
      >
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <label htmlFor="sofia-input" className="sr-only">Message Sofia</label>
          <textarea
            id="sofia-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            rows={1}
            placeholder="Something you believe, or something you’re stuck on…"
            className="flex-1 resize-none rounded-xl border border-white/15 bg-charcoal text-pearl px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-bright-gold/70 focus:border-bright-gold/70 max-h-40 placeholder-pearl/60"
            disabled={sending}
            maxLength={2000}
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="flex-shrink-0 p-3 bg-gold hover:bg-bright-gold text-gunmetal rounded-xl disabled:opacity-50 transition-colors"
            aria-label="Send"
          >
            <Send size={18} />
          </button>
        </div>
        {error && <p role="alert" className="max-w-3xl mx-auto text-xs sm:text-sm text-red-300 mt-1">{error}</p>}
      </form>
    </div>
  );
}
